function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useMemo } from "react";
import { useHotkeys } from "@blueprintjs/core";
import { startCase } from "lodash"; // This has been mostly superseded by blueprint's KeyCombo component, but may
// still be useful for cases where we need plain text

export function comboToLabel(def, useSymbols) {
  if (useSymbols === void 0) {
    useSymbols = true;
  }

  var combo = typeof def === "string" ? def : def.combo;

  if (useSymbols) {
    var parts = combo.replace("++", "+plus").split("+");
    parts = parts.map(function (p) {
      return symbols[p] || startCase(p) || p;
    });
    return parts.join("");
  } else {
    return combo.split("+").map(function (p) {
      return startCase(p) || p;
    }).join(" + ").replace("Meta", isMac ? "Cmd" : "Ctrl").replace("Mod", isMac ? "Cmd" : "Ctrl").replace("Alt", isMac ? "Option" : "Alt");
  }
} // HOF to get hotkey combos by id

export var hotkeysById = function hotkeysById(hotkeys, mode) {
  if (mode === void 0) {
    mode = "raw";
  }

  return function (id) {
    var def = getHotkeyProps(hotkeys[id]);
    return def && (mode === "raw" ? def.combo : comboToLabel(def.combo, mode === "symbols"));
  };
}; // Translate shorthand array if needed

export var getHotkeyProps = function getHotkeyProps(def, id) {
  var out;

  if (typeof def === "string") {
    out = {
      combo: def
    };
  } else if (def instanceof Array) {
    out = _extends({
      combo: def[0],
      label: def[1]
    }, def[2] || {});
  } else {
    out = def;
  }

  out.label = out.label || startCase(id);
  return out;
};
/*
 * HOC to add hotkey support to components. Use this instead of blueprint's one.
 *
 * Arguments:
 * - hotkeySpec: either a named hotkey section previously registered, or an
 *   object mapping command ids to hotkey definitions, where each hotkey can
 *   be either:
 *   - a string consisting in the key combo (e.g. 'ctrl+shift+x')
 *   - an array holding the combo, label, and an object with any other props
 *   - an object holding all props
 * - handlers: an object mapping command ids to handler functions
 * - options: an object that may specify the follownig options:
 *   - functional: boolean indicating if the wrapped component will be a
 *     functional stateless component instead of a class-based one
 *
 * Returns a function that can be invoked with a component class, or a
 * stateless component function (if specified in the options) and returns
 * the decorated class. It may also be invoked without arguments to generate a
 * dummy ad-hoc component with no output.
 *
 */

export var withHotkeys = function withHotkeys(hotkeys, handlers) {
  return function (_temp) {
    var _ref = _temp === void 0 ? {} : _temp,
        children = _ref.children;

    var memoedHotkeys = useMemo(function () {
      return Object.keys(hotkeys).map(function (id) {
        var _getHotkeyProps = getHotkeyProps(hotkeys[id], id),
            props = _extends({}, _getHotkeyProps);

        return _extends({
          key: id,
          global: props.global !== false,
          onKeyDown: function onKeyDown(e) {
            return handlers[id](e);
          }
        }, props);
      });
    }, []);

    var _useHotkeys = useHotkeys(memoedHotkeys),
        handleKeyDown = _useHotkeys.handleKeyDown,
        handleKeyUp = _useHotkeys.handleKeyUp;

    var newProps = {
      tabIndex: 0,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp
    };
    return children ? //tnr: if children are passed, we'll clone them with the new props
    React.cloneElement(children, newProps) :
    /*#__PURE__*/
    //if not, then we'll return a div that can be used
    React.createElement("div", _extends({
      className: "hotkeyHandler"
    }, newProps));
  };
};
var isMac = navigator.userAgent.includes("Mac OS X"); // TODO maybe avoid using symbols by default when not on Mac?
// Anyway, alternative 'Key + Key' description is provided as well

var symbols = {
  cmd: "⌘",
  meta: "⌘",
  ctrl: "⌃",
  alt: "⌥",
  shift: "⇧",
  esc: "␛",
  //'⎋',
  enter: "⏎",
  backspace: "⌫",
  plus: "+",
  tab: "⇥",
  space: "␣",
  capslock: "⇪",
  pageup: "⇞",
  pagedown: "⇟",
  home: "↖",
  end: "↘",
  left: "←",
  right: "→",
  up: "↑",
  down: "↓"
};
symbols.mod = symbols[isMac ? "meta" : "ctrl"];